import { useState } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Card, 
  CardContent,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  Alert,
  CircularProgress,
  Dialog
} from '@mui/material';
import { object, string } from 'yup';
import Receipt from './Receipt';

const validationSchema = object({
  phoneNumber: string()
    .required('Phone number is required')
    .matches(/^01[3-9]\d{8}$/, 'Invalid Bangladesh phone number'),
  transactionId: string()
    .required('Transaction ID is required')
    .min(6, 'Transaction ID must be at least 6 characters')
});

function BangladeshPayment({ bookingData, onPaymentComplete }) {
  const [paymentMethod, setPaymentMethod] = useState('bkash');
  const [showReceipt, setShowReceipt] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState({
    phoneNumber: '',
    transactionId: ''
  });

  const paymentInstructions = {
    bkash: {
      merchantNumber: '01712345678',
      steps: [
        'Go to bKash App or *247#',
        'Choose "Send Money"',
        'Enter merchant number: 01712345678',
        `Enter amount: ৳${bookingData.totalAmount}`,
        'Enter your PIN to confirm',
        'Save the Transaction ID',
        'Enter the Transaction ID below'
      ]
    },
    nagad: {
      merchantNumber: '01XXXXXXXXX',
      steps: [
        'Go to Nagad App or *167#',
        'Choose "Send Money"',
        'Enter merchant number: 01XXXXXXXXX',
        'Enter total amount',
        'Enter your PIN to confirm',
        'Save the Transaction ID',
        'Enter the Transaction ID below'
      ]
    },
    rocket: {
      merchantNumber: '01XXXXXXXXX',
      steps: [
        'Go to Rocket App or *322#',
        'Choose "Send Money"',
        'Enter merchant number: 01XXXXXXXXX',
        'Enter total amount',
        'Enter your PIN to confirm',
        'Save the Transaction ID',
        'Enter the Transaction ID below'
      ]
    },
    bank: {
      accountInfo: {
        bankName: 'Example Bank',
        accountName: 'RentWheels Ltd',
        accountNumber: 'XXXXXXXXXXXX',
        branchName: 'Main Branch',
        routingNumber: 'XXXXXXXXX'
      },
      steps: [
        'Transfer the total amount to our bank account',
        'Save the Transaction ID/Reference number',
        'Enter the Transaction ID below'
      ]
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validate fields
      await validationSchema.validate(paymentDetails);

      // Send payment details to backend
      const response = await fetch('http://localhost:5000/api/bangladesh-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...paymentDetails,
          paymentMethod,
          amount: bookingData.totalAmount,
          bookingDetails: {
            ...bookingData,
            paymentDate: new Date().toISOString()
          }
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Payment verification failed');
      }

      // Update booking data with payment information
      const updatedBookingData = {
        ...bookingData,
        bookingNumber: data.booking.bookingNumber,
        paymentMethod,
        paymentStatus: 'completed',
        transactionId: paymentDetails.transactionId,
        payerPhone: paymentDetails.phoneNumber
      };

      setShowReceipt(true);
      onPaymentComplete?.(updatedBookingData);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const currentInstructions = paymentInstructions[paymentMethod];

  return (
    <>
      <Card sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Payment Details
          </Typography>

          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend">Select Payment Method</FormLabel>
            <RadioGroup
              row
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              <FormControlLabel value="bkash" control={<Radio />} label="bKash" />
              <FormControlLabel value="nagad" control={<Radio />} label="Nagad" />
              <FormControlLabel value="rocket" control={<Radio />} label="Rocket" />
              <FormControlLabel value="bank" control={<Radio />} label="Bank Transfer" />
            </RadioGroup>
          </FormControl>

          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              Payment Instructions
            </Typography>
            {paymentMethod === 'bank' ? (
              <>
                <Typography variant="subtitle2">Bank Details:</Typography>
                {Object.entries(currentInstructions.accountInfo).map(([key, value]) => (
                  <Typography key={key} variant="body2">
                    {key.replace(/([A-Z])/g, ' $1').trim()}: {value}
                  </Typography>
                ))}
              </>
            ) : (
              <Typography variant="subtitle2" gutterBottom>
                Merchant Number: {currentInstructions.merchantNumber}
              </Typography>
            )}
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Steps:</Typography>
            <ol>
              {currentInstructions.steps.map((step, index) => (
                <li key={index}>
                  <Typography variant="body2">{step}</Typography>
                </li>
              ))}
            </ol>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handlePayment}>
            <TextField
              fullWidth
              name="phoneNumber"
              label="Phone Number"
              variant="outlined"
              margin="normal"
              required
              value={paymentDetails.phoneNumber}
              onChange={handleInputChange}
              helperText="Enter the number you used for payment"
            />

            <TextField
              fullWidth
              name="transactionId"
              label="Transaction ID"
              variant="outlined"
              margin="normal"
              required
              value={paymentDetails.transactionId}
              onChange={handleInputChange}
            />

            <Typography variant="h6" sx={{ mt: 2, color: 'primary.main' }}>
              Total Amount: ৳{bookingData.totalAmount}
            </Typography>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 3 }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Verify Payment'
              )}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={showReceipt}
        onClose={() => setShowReceipt(false)}
        maxWidth="md"
        fullWidth
      >
        <Receipt bookingData={bookingData} />
      </Dialog>
    </>
  );
}

export default BangladeshPayment; 